// =============================================================================
// Voice write budget — the client half of issue #933.
//
// The enforcement is in Postgres (20260816120000_voice_write_limits.sql). This
// file is what the browser needs in order to (a) mint the propose id that makes
// a duplicated tool call idempotent, and (b) turn the database's refusal into
// something Kin can SAY.
//
// ── Why the strings are prose, not codes ─────────────────────────────────────
//
// Every string here goes back to the realtime model as a tool result and is
// about to be spoken out loud. An error code would get read aloud. Same posture
// as SEARCH_CAP_TOOL_RESULT (voiceSearchCap.ts) and rememberFamilyFact's
// REJECTION_MESSAGES — the model is told what happened and what to do about it,
// in words, and the call keeps going.
//
// ⚠️ A refusal must never reach the model as a thrown error. A tool handler
// that throws leaves the model waiting on a function_call_output that is not
// coming, and Kin goes silent mid-call. Silence is how a voice assistant reads
// as broken; a spoken "I've hit my limit" does not.
// =============================================================================

/**
 * ⚠️ These two MUST match `v_cap` and `v_window` in all three SQL sites:
 * `enforce_voice_fact_budget()`, `enforce_voice_reminder_budget()`, and
 * `upsert_family_fact()`. A vitest reads the migration and asserts all four
 * agree, so drifting one of them fails CI rather than shipping a client that
 * misdescribes the server.
 *
 * They are NOT used to enforce anything here — the client cannot enforce a cap
 * against itself, which is the whole point of the issue. They exist so the
 * numbers have one readable home in TypeScript and so the test has something to
 * compare against.
 */
export const VOICE_WRITE_CAP = 12;
export const VOICE_WRITE_WINDOW_MINUTES = 10;

// #1694 — these four moved to supabase/functions/_shared/voiceWriteErrors.ts so
// the Edge module behind voice's event write (kin-chat/createEventWrite.ts) can
// recognise the SAME two refusals without a second copy: Edge code cannot import
// from src/, and `src/` importing an Edge module is the direction this repo
// already uses. Re-exported so every existing caller is unchanged.
export {
  PROPOSE_INDEXES,
  UNIQUE_VIOLATION,
  VOICE_RATE_LIMIT_CODE,
  VOICE_RATE_LIMIT_MARKER,
  isDuplicateProposeError,
  isVoiceRateLimitError,
} from "../../supabase/functions/_shared/voiceWriteErrors";

/**
 * A fresh propose id.
 *
 * `crypto.randomUUID` is present in every browser this app supports and in the
 * test environment. The fallback exists for insecure-origin edge cases (it is
 * unavailable outside a secure context in some engines) and is deliberately
 * plain: this is a correlation id inside one user's own rows, not a secret, and
 * the unique index is scoped per user. A collision would need the same account
 * to draw the same 122 bits twice.
 */
export function mintProposeId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = Math.floor(Math.random() * 16);
    const v = ch === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Spoken when the per-window cap refuses a write. */
export const VOICE_WRITE_RATE_LIMITED_RESULT =
  "You've saved a lot in this one conversation already and you've hit your limit for now. " +
  "Tell her you'll hold off on saving anything else for a few minutes, and carry on talking normally. Don't retry.";

/** Spoken when the same proposal was already saved — a success, phrased as one. */
export const VOICE_WRITE_ALREADY_SAVED_RESULT =
  "You already saved that one. Don't save it again and don't mention the duplicate — just carry on.";
